import React, { useEffect } from 'react';
import { ArrowLeft, AlertTriangle, BookOpen, Globe, ExternalLink, DollarSign, Scale, Mail, Shield } from 'lucide-react';
import { Button } from './ui/button';
import { Link } from 'react-router-dom';

const Disclaimer = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const disclaimers = [
    {
      icon: Shield,
      title: "Consulting & Audit Services",
      content: "Consulting and audit services provide professional guidance; results, including certifications or compliance, are ultimately the client's responsibility.",
      color: "from-blue-500/20 to-turquoise-500/20"
    },
    {
      icon: BookOpen,
      title: "Website Content",
      content: "Website content is general and educational. Tailored legal, financial, or regulatory advice requires a formal agreement.",
      color: "from-green-500/20 to-turquoise-500/20"
    },
    {
      icon: AlertTriangle,
      title: "Information Accuracy",
      content: "While we strive for accuracy, errors may occur. Use information at your own discretion.",
      color: "from-orange-500/20 to-turquoise-500/20"
    },
    {
      icon: ExternalLink,
      title: "External Links",
      content: "Links to external sites are for convenience; we are not responsible for their content.",
      color: "from-purple-500/20 to-turquoise-500/20"
    },
    {
      icon: DollarSign,
      title: "Liability Limitation",
      content: "Liability is limited to the fees paid for services.",
      color: "from-red-500/20 to-turquoise-500/20"
    },
    {
      icon: Scale,
      title: "Governing Law",
      content: "This disclaimer is governed by the laws of India, under the jurisdiction of Tamil Nadu courts.",
      color: "from-indigo-500/20 to-turquoise-500/20"
    }
  ];

  return (
    <div className="dark-container min-h-screen" style={{ paddingTop: '100px' }}>
      <div className="max-w-6xl mx-auto px-6 py-12">
        {/* Back Button */}
        <Link to="/">
          <Button className="btn-secondary mb-8">
            <ArrowLeft size={20} />
            Back to Home
          </Button>
        </Link>

        {/* Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center mb-6">
            <div className="w-20 h-20 bg-gradient-to-br from-orange-500/20 to-red-500/20 rounded-full flex items-center justify-center border border-orange-500/30">
              <AlertTriangle size={40} className="text-orange-400" />
            </div>
          </div>
          <h1 className="display-large mb-6">
            <span className="text-orange-400">Disclaimer</span>
          </h1>
          <p className="body-large text-gray-300 max-w-3xl mx-auto">
            Here's what you need to know about our services and website to make informed decisions.
          </p>
        </div>

        {/* Important Notice */}
        <div className="bg-gradient-to-r from-orange-500/10 to-red-500/10 border border-orange-500/30 rounded-16 p-8 mb-12">
          <div className="flex items-center gap-4 mb-4">
            <AlertTriangle size={24} className="text-orange-400" />
            <h3 className="heading-3 text-white">Important Notice</h3>
          </div>
          <p className="body-medium text-gray-300">
            Please read this disclaimer carefully before using our services or website. It contains important information about limitations and responsibilities.
          </p>
        </div>

        {/* Disclaimer Sections Grid */}
        <div className="grid md:grid-cols-2 gap-8 mb-16">
          {disclaimers.map((disclaimer, index) => (
            <div key={index} className={`disclaimer-card bg-gradient-to-br ${disclaimer.color} border border-turquoise-500/20 rounded-16 p-8 hover:border-turquoise-500/40 transition-all duration-300`}>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-turquoise-500/20 rounded-full flex items-center justify-center flex-shrink-0 border border-turquoise-500/30">
                  <disclaimer.icon size={24} className="text-turquoise-400" />
                </div>
                <div className="flex-1">
                  <h3 className="heading-3 mb-4 text-white">{disclaimer.title}</h3>
                  <p className="body-medium text-gray-300 leading-relaxed">{disclaimer.content}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Professional Advice Notice */}
        <div className="bg-gradient-to-r from-blue-500/10 to-turquoise-500/10 border border-blue-500/30 rounded-16 p-8 mb-12">
          <div className="flex items-center gap-4 mb-4">
            <BookOpen size={24} className="text-blue-400" />
            <h3 className="heading-3 text-white">Professional Advice</h3>
          </div>
          <p className="body-medium text-gray-300">
            The information provided on this website and through our services is for general guidance only. For specific situations, please seek tailored professional advice through a formal engagement.
          </p>
        </div>

        {/* Contact Section */}
        <div className="text-center bg-gradient-to-br from-gray-900/50 to-turquoise-500/5 border border-turquoise-500/20 rounded-16 p-12">
          <div className="w-16 h-16 bg-turquoise-500/20 rounded-full flex items-center justify-center mx-auto mb-6 border border-turquoise-500/30">
            <Mail size={32} className="text-turquoise-400" />
          </div>
          <h3 className="heading-2 mb-4 text-white">Need Clarification?</h3>
          <p className="body-large text-gray-300 mb-6">
            Have questions about this disclaimer or need specific guidance? Contact us directly.
          </p>
          <Button 
            className="btn-primary-enhanced"
            onClick={() => window.open('mailto:shunmugavelsubramanian@gmail.com', '_blank')}
          >
            <Mail size={20} />
            Contact Us
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Disclaimer;